import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Radio } from "lucide-react";

const STATUS_STYLES = {
    pass: "bg-emerald-100 text-emerald-800",
    pending: "bg-amber-100 text-amber-800",
    fail: "bg-rose-100 text-rose-800",
    stop: "bg-rose-600 text-white",
};

const PreflightResult = ({ r }) => (
    <div className="space-y-2" data-testid="twilio-preflight-result">
        <div
            className={"text-xs font-semibold " + (r.ready_for_live_test ? "text-emerald-700" : "text-rose-700")}
            data-testid="twilio-preflight-ready"
        >
            {r.ready_for_live_test ? "All preflight checks passed" : "Not ready for a live test"}
        </div>
        <ul className="space-y-1">
            {(r.checks || []).map(c => (
                <li key={c.check} className="flex items-start gap-2 text-xs" data-testid={`preflight-check-${c.check}`}>
                    <span className={"shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] uppercase " + (STATUS_STYLES[c.status] || "")}>
                        {c.status}
                    </span>
                    <span><span className="font-medium">{c.check}</span>: {c.detail}</span>
                </li>
            ))}
        </ul>
        {r.facts ? (
            <pre className="text-[10px] bg-gray-50 border border-border rounded p-2 overflow-x-auto max-h-64" data-testid="twilio-preflight-facts">
                {JSON.stringify(r.facts, null, 2)}
            </pre>
        ) : null}
        {r.error ? <div className="text-xs text-rose-700">{r.error}</div> : null}
    </div>
);

export const TwilioPreflightPanel = () => {
    const [phone, setPhone] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/twilio-preflight", { params: { phone } });
            setResult(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="rounded-lg border border-border bg-background p-4 space-y-3" data-testid="twilio-preflight-panel">
            <div className="flex items-center gap-2 text-sm font-medium"><ShieldCheck className="h-4 w-4" /> Spare-number preflight (read-only)</div>
            <p className="text-xs text-muted-foreground">
                Read-only Twilio checks using the saved Preview credentials: account, owning Messaging Service and how it
                handles incoming messages, A2P 10DLC / toll-free approval, and the signature URL. Changes nothing in Twilio.
            </p>
            <div className="flex items-end gap-2 max-w-md">
                <div className="flex-1">
                    <Label>Spare Twilio number</Label>
                    <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1…" data-testid="twilio-preflight-phone-input" />
                </div>
                <Button onClick={run} disabled={busy || !phone.trim()} data-testid="twilio-preflight-run-btn">
                    {busy ? "Checking…" : "Run preflight"}
                </Button>
            </div>
            {result ? <PreflightResult r={result} /> : null}
        </div>
    );
};

export const LiveAllowanceControl = () => {
    const [st, setSt] = useState(null);
    const load = async () => {
        try { setSt((await api.get("/admin/inbound-notify/live-allowance")).data); } catch (e) { setSt(null); }
    };
    useEffect(() => { load(); }, []);
    const act = async (path) => {
        try {
            setSt((await api.post(`/admin/inbound-notify/live-allowance/${path}`)).data);
            toast.success(path === "arm" ? "One real notification armed for 30 minutes" : "Live allowance disarmed");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    if (!st) return null;
    return (
        <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50/40 p-4 space-y-2" data-testid="live-allowance-panel">
            <div className="flex items-center gap-2 text-sm font-medium"><Radio className="h-4 w-4" /> One-time live test notification</div>
            <p className="text-xs text-muted-foreground">
                While Test Mode is ON, arming lets exactly one notification for a real, Twilio-signed inbound reply be sent
                for real (expires in 30 minutes). Simulated replies and every other send stay mocked.
            </p>
            <div className="text-xs" data-testid="live-allowance-status">
                {st.armed
                    ? <span className="font-semibold text-amber-800">ARMED: {st.remaining} send left, expires {new Date(st.expires_at).toLocaleTimeString()}</span>
                    : <span className="text-gray-600">Unarmed{st.consumed_at ? ` (last used ${new Date(st.consumed_at).toLocaleString()})` : ""}</span>}
            </div>
            <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={st.armed} onClick={() => act("arm")} data-testid="live-allowance-arm-btn">
                    Allow 1 real test notification
                </Button>
                <Button size="sm" variant="ghost" disabled={!st.armed} onClick={() => act("disarm")} data-testid="live-allowance-disarm-btn">
                    Disarm
                </Button>
                <Button size="sm" variant="ghost" onClick={load} data-testid="live-allowance-refresh-btn">Refresh</Button>
            </div>
        </div>
    );
};
